import React, { useRef, useState, useCallback, ReactNode } from 'react';
import PopNotification from './components/PopNotification';
import { PopNotificationRef } from './components/PopNotification/PopNotification.component';
import PopNotificationsContext, {
  Notification,
} from './PopNotifications.context';
import uniqid from 'uniqid';
import { AxiosError } from 'axios';
import {
  getAxiosErrorMessageCode,
  getAxiosErrorStatusCode,
} from 'uploadFileUtils';
import { useTranslation } from 'react-i18next';
import {
  ERROR_TYPES,
  ErrorMessageKey,
  ERRORS_USER_FRIENDLY,
} from 'request-responses';
import GenericErrorContent from './components/GenericErrorContent';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { ErrorViewedProps } from 'models/MixpanelEvents';

const DISSAPPEAR_PERIOD = 5000; // 5 seconds

const PopNotifications: React.FC = (props) => {
  const { children } = props;

  const [notifications, setNotifications] = useState<
    Array<Notification & { notId: string }>
  >([]);

  const notificationsRef = useRef(new Set<PopNotificationRef>());

  const getMixpanelProps = useGetMixpanelProps();

  const { t } = useTranslation();

  const onNotificationClose = useCallback((n: Notification) => {
    n.onClose?.();
    setNotifications((old) => old.filter((not) => not !== n));
  }, []);

  const popInfo = useCallback((conf: Notification) => {
    Array.from(notificationsRef.current)
      .find((n) => n.open)
      ?.setOpen(false);
    setNotifications((old) => [...old, { ...conf, notId: uniqid() }]);
  }, []);

  const popSuccess = useCallback(
    (content: string | ReactNode) => {
      popInfo({
        type: 'Success',
        content,
      });
    },
    [popInfo],
  );

  const popError = useCallback(
    (content?: string | ReactNode) => {
      popInfo({
        type: 'Error',
        content: content || <GenericErrorContent />,
      });
    },
    [popInfo],
  );

  const popServerError = useCallback(
    (error: AxiosError | unknown, shouldTrackEvent = true) => {
      const id = getAxiosErrorMessageCode(error as AxiosError);
      const errorCode = getAxiosErrorStatusCode(error as AxiosError);

      const errorKey = Object.keys(ERROR_TYPES).find(
        (k) => ERROR_TYPES[k as ErrorMessageKey] === id,
      );

      if (shouldTrackEvent) {
        mixpanelService.errorViewed(
          getMixpanelProps({
            id: [id, errorKey].filter(Boolean).join(' - ') || '',
            errorCode,
          }) as ErrorViewedProps,
        );
      }

      const errorMessage = ERRORS_USER_FRIENDLY[id];

      errorMessage ? popError(t(errorMessage)) : popError();
    },
    [getMixpanelProps, popError, t],
  );

  return (
    <PopNotificationsContext.Provider
      value={{ popInfo, popSuccess, popError, popServerError }}
    >
      {notifications.map((n, ind) => (
        <PopNotification
          side="top"
          className="pop-notif-plat"
          dissappearAfter={DISSAPPEAR_PERIOD}
          ref={(r) => r && notificationsRef.current.add(r)}
          key={n.notId}
          {...n}
          onClose={() => {
            notificationsRef.current.delete(
              Array.from(notificationsRef.current)[ind],
            );
            onNotificationClose?.(n);
          }}
        >
          {n.content}
        </PopNotification>
      ))}
      {children}
    </PopNotificationsContext.Provider>
  );
};

export default PopNotifications;
